// @flow
import type {HttpService} from 'modules/common/services/http';
import type {
    TAddUsersParams,
    TChangeUserNameParams,
    TChangeUserEmailParams,
    TEducationStartParams,
    TUserId,
    TUserListParams,
} from 'modules/user/types';
import type {TResponse} from 'types';
import {object} from 'yup';

/**
 * Сервис для загрузки информации о пользователях
 */
export class UserApi {
    /**
     * Сервис для отправки HTTP запросов
     */
    _http: HttpService;

    /**
     * @param http Сервис для отправки HTTP запросов
     */
    constructor(http: HttpService) {
        this._http = http;
    }

    /**
     * Fetch запрос "Просмотр информации о пользователях компании"
     * @param limit лимит получаемых элементов
     * @param offset смещение относительно начала списка
     * @param params доп. параметры для фильтрации/сортировки/поиска
     * @return список всех пользователей
     */
    async getUserList<T>({limit, offset, params}: TUserListParams): TResponse<T> {
        return this._http.get<T>('/user/list', {
            extraOptions: {
                isPrivate: true,
            },
            query: {
                ...params,
                limit,
                offset,
            },
        });
    }

    /**
     * Fetch запрос "Добавление нового пользователя админом"
     * @param users объект пользователей для добавления
     * @return данные добавленных юзеров
     */
    async addUsers<T>(users: TAddUsersParams): TResponse<T> {
        return this._http.post<T>('/user/create', {
            body: users,
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Добавление нового пользователя админом"
     * @param users объект пользователей для добавления
     * @return данные импортированных юзеров
     */
    async importUsers<T>(users: TAddUsersParams): TResponse<T> {
        return this._http.post<T>('/user/import', {
            body: users,
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Добавление пользователей админом из xlsx"
     * @param data объект пользователей для добавления
     * @return данные импортированных юзеров
     */
    async importXlsxUsers<T>(data: object): TResponse<T> {
        return this._http.post<T>('/user/import-xlsx', {
            body: data,
            extraOptions: {
                dataType: 'FORM_DATA',
                isPrivate: true,
            },
            options: {
                headers: {
                    'X-http-method-override': 'POST',
                    credentials: 'include',
                },
            },
        });
    }

    /**
     * Fetch запрос "Старт обучения"
     * @param params id лицензии / id юзера
     * @return статус об успешном/не успешном исходе
     */
    async educationStart<T>({licenseId, userId, userIds}: TEducationStartParams): TResponse<T> {
        return this._http.post<T>('/status-education/start', {
            body: {licenseId, userId, userIds},
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Остановка обучения"
     * @param params id юзера / id юзеров
     * @return статус об успешном/не успешном исходе
     */
    async educationStop<T>({userId, userIds}: {userId?: TUserId, userIds?: TUserId[]}): TResponse<T> {
        return this._http.post<T>('/status-education/stop', {
            body: {userId, userIds},
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Завершение обучения"
     * @param userId id юзера
     * @return статус об успешном/не успешном исходе
     */
    async educationComplete<T>(userId: TUserId): TResponse<T> {
        return this._http.post<T>('/status-education/complete', {
            body: {userId},
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Возобновление обучения"
     * @param params id юзера / id юзеров
     * @return статус об успешном/не успешном исходе
     */
    async educationRenew<T>({userId, userIds}: {userId?: TUserId, userIds?: TUserId[]}): TResponse<T> {
        return this._http.post<T>('/status-education/renew', {
            body: {userId, userIds},
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Изменить имя конкретного юзера"
     * @param userData данные юзера для изменения
     * @return статус об успешном/не успешном исходе
     */
    async changeUserName<T>({userId, firstName}: TChangeUserNameParams): TResponse<T> {
        return this._http.post<T>(`/user/update/${userId}`, {
            body: {firstName},
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Изменить e-mail конкретного юзера"
     * @param userData данные юзера для изменения
     * @return статус об успешном/не успешном исходе
     */
    async changeUserEmail<T>({userId, email}: TChangeUserEmailParams): TResponse<T> {
        return this._http.post<T>(`/user/update/${userId}`, {
            body: {email},
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Повторная отправка администратором уведомления о верификации аккаунта пользователя"
     * @param userId id юзера для повторной отправки
     * @return статус об успешном/не успешном исходе
     */
    async reVerification<T>(userId: TUserId): TResponse<T> {
        return this._http.post<T>(`/user/re-verification/${userId}`, {
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Удалить конкретного юзера|юзеров"
     * @param userId id юзера для удаления
     * @param userIds объект пользователей для удаления
     * @return статус об успешном/не успешном исходе
     */
    async deleteUser<T>({userId, userIds}: {userId?: TUserId, userIds?: TUserId[]}): TResponse<T> {
        return this._http.post<T>(`/user/delete`, {
            body: {userId, userIds},
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Получить общие данные конкретного юзера"
     * @param userId id юзера
     * @return общие данные по юзеру
     */
    async getCommonUserData<T>(userId: TUserId): TResponse<T> {
        return this._http.get<T>(`/user/${userId}`, {
            extraOptions: {
                isPrivate: true,
            },
        });
    }

    /**
     * Fetch запрос "Получить данные по обучению конкретного юзера"
     * @param userId id юзера
     * @return данные по обучению юзера
     */
    async getEducationData<T>(userId: TUserId): TResponse<T> {
        return this._http.post<T>('/status-education', {
            body: {userId},
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Запросить ссылку на отчет по всем юзерах"
     * @return ссылка на файл отчета
     */
    async reportUserList<T>(): TResponse<T> {
        return this._http.post<T>('/user/report-user-list', {
            extraOptions: {
                isPrivate: true,
            },
        });
    }

    /**
     * Fetch запрос "Запросить ссылку на отчет о конкретном юзере"
     * @param userId id юзера
     * @return ссылка на файл отчета
     */
    async reportUser<T>(userId: TUserId): TResponse<T> {
        return this._http.post<T>(`/user/report-user-one/${userId}`, {
            extraOptions: {
                isPrivate: true,
            },
        });
    }
}
